/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Grid, ICON_ACCOUNT_OUTLINE, ICON_BULLSEYE_ARROW, Message, Skeleton, Stack, Text,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import * as styles from './AgentGoalSimulationResultView.style';
import AgentProfileBySalesCard from '../agent-profile-by-sales-card/AgentProfileBySalesCard';
import GoalMetricCard from '../goal-metric-card/GoalMetricCard';
import AgentProfileBySalesCardSkeleton from '../agent-profile-by-sales-card/AgentProfileBySalesCard.skeleton';
import GoalMetricCardSkeleton from '../goal-metric-card/GoalMetricCard.skeleton';
import { Agent, GOAL_METRICS, IndividualGoal } from '../../../providers/api/dtos';
import AcceptAgentGoalAction, { AcceptAgentGoalActionProps } from '../../containers/accept-agent-goal-action/AcceptAgentGoalAction';

export interface AgentGoalSimulationResultViewProps extends React.AriaAttributes {
  agentId: Agent['id'],
  target: number,
  goal?: IndividualGoal,
  loading?: boolean,
  onGoalAccepted?: AcceptAgentGoalActionProps['onSuccess'],
}

export const AgentGoalSimulationResultView: React.FC<AgentGoalSimulationResultViewProps> = ({
  agentId,
  target,
  goal,
  loading = false,
  onGoalAccepted,
  'aria-label': ariaLabel = 'agent-goal-simulation-result-view',
}) => (
  <div
    css={styles.simulation}
    $isLoading={loading}
    aria-label={ariaLabel}
  >
    <div css={styles.simulationContent}>
      <Grid gutter={150}>
        <Grid.Unit size={{ LG: 1 / 2 }}>
          <Stack gap={200}>
            <Message
              message="Punti ad essere un agente di questo tipo"
              icon={ICON_ACCOUNT_OUTLINE}
              type="primary"
            />
            {loading && (
              <AgentProfileBySalesCardSkeleton />
            )}
            {!loading && goal && (
              <AgentProfileBySalesCard targetAgentType={goal.targetAgentType} />
            )}
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={{ LG: 1 / 2 }}>
          <Stack gap={200}>
            <Stack gap={150}>
              <Message
                message={`Parametri di conversione per ${formatEuro(target)}`}
                icon={ICON_BULLSEYE_ARROW}
                type="primary"
              />
              <Text.BodySmall>
                Per raggiungere il tuo obiettivo di guadagno annuale, ti è necessario effettuare un numero minimo di notizie, valutazioni, mandati e vendite annuali.
              </Text.BodySmall>
            </Stack>
            <Grid gutter={75}>
              {(!loading && goal) && GOAL_METRICS.map((metric) => {
                const frequency = ['assignments', 'sales'].includes(metric) ? 'monthly' : 'weekly';

                return (
                  <Grid.Unit key={metric} size={{ MD: 1 / 2 }}>
                    <GoalMetricCard
                      avgCommission={goal.averageCommission}
                      metric={metric}
                      target={goal.metrics[metric].yearly.goal}
                      frequency={frequency}
                      targetByFrequency={goal.metrics[metric][frequency].forecast}
                    />
                  </Grid.Unit>
                );
              })}
              {loading && (
                <>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <GoalMetricCardSkeleton />
                  </Grid.Unit>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <GoalMetricCardSkeleton />
                  </Grid.Unit>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <GoalMetricCardSkeleton />
                  </Grid.Unit>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <GoalMetricCardSkeleton />
                  </Grid.Unit>
                </>
              )}
            </Grid>
          </Stack>
        </Grid.Unit>
      </Grid>
    </div>
    <div
      css={styles.simulationAction}
      $isLoading={loading}
    >
      {!loading && goal && (
        <AcceptAgentGoalAction
          agentId={agentId}
          target={goal.target}
          onSuccess={onGoalAccepted}
        />
      )}
      {/** TODO action skeleton: https://doveit.atlassian.net/browse/DOVE-14727 */}
      {loading && (
        <Skeleton
          height="3rem"
          width="12rem"
        />
      )}
    </div>
  </div>
);
